import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import SidePanel from '../util/SidePanel';
import CalendarPicker from '../util/CalendarPicker';
import { getISOWeek } from 'date-fns';
import UserAvatar from '../util/UserAvatar';

interface WeekHeader {
  weekKey: string;
  weekNum: number;
  year: number;
  start: string;
  end: string;
  isCurrent: boolean;
}

interface WeekCell {
  weekKey: string;
  status: 'FULFILLED' | 'PENDING' | 'REJECTED' | 'EMPTY' | 'CURRENT';
  totalHours: number | null;
  toilDelta: number | null;
  hoursPerWeek?: number | null;
}

interface UserRow {
  id: number;
  firstName: string;
  lastName: string;
  jobTitle: string;
  hoursPerWeek: number;
  patternId:    number | null;
  patternTitle: string | null;
  netToil: number;
  submitted: number;
  totalWeeks: number;
  fulfilled: number;
  pending: number;
  rejected: number;
  weeks: WeekCell[];
}

interface CalendarData {
  weeks: WeekHeader[];
  users: UserRow[];
}

const STATUS_COLORS = {
  FULFILLED: { bg: 'rgba(5,150,105,.14)',  text: '#059669', dot: '#059669' },
  PENDING:   { bg: 'rgba(217,119,6,.13)',  text: '#d97706', dot: '#d97706' },
  REJECTED:  { bg: 'rgba(239,68,68,.12)', text: '#ef4444', dot: '#ef4444' },
  EMPTY:     { bg: 'var(--btn-user-form)', text: 'var(--text-color-calendar-week)', dot: '' },
  CURRENT:   { bg: 'var(--btn-user-form)', text: 'var(--text-color)', dot: '' },
};

function fmt(n: number | null): string {
  if (n === null) return '—';
  return n % 1 === 0 ? `${n}h` : `${n}h`;
}

function fmtDelta(n: number | null): { text: string; color: string } {
  if (n === null) return { text: '—', color: 'var(--text-color-calendar-week)' };
  if (n > 0) return { text: `+${n}h`, color: '#059669' };
  if (n < 0) return { text: `${n}h`, color: '#ef4444' };
  return { text: '0h', color: 'var(--text-color-calendar-week)' };
}

function initials(first: string, last: string) { return `${first?.[0] ?? ''}${last?.[0] ?? ''}`; }

const COLORS = ['#2563eb,#7c3aed','#be185d,#f472b6','#065f46,#34d399','#1e40af,#60a5fa','#78350f,#fbbf24','#6d28d9,#a78bfa','#9d174d,#f472b6','#1e3a5f,#38bdf8'];
function userColor(id: number) { return COLORS[id % COLORS.length]; }

interface Props {
  auth: boolean;
  access: any;
  offset?: number;
  onOffsetChange?: (offset: number) => void;
  onPeriodLabel?: (label: string) => void;
  searchFilter?: string;
  contractFilter?: number[];
}

const WEEKS = 8;

const ToilCalendar: React.FC<Props> = ({ auth, access, offset: offsetProp, onOffsetChange, onPeriodLabel, searchFilter, contractFilter }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<CalendarData | null>(null);
  const [internalOffset, setInternalOffset] = useState(0);
  const offset = offsetProp ?? internalOffset;
  const setOffset = onOffsetChange ?? setInternalOffset;
  const [expanded, setExpanded] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [recounting, setRecounting] = useState<Record<number, boolean>>({});

  // Full history drawer
  const [historyUser, setHistoryUser] = useState<UserRow | null>(null);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [historyFrom, setHistoryFrom] = useState('');
  const [historyTo, setHistoryTo] = useState('');
  const [openComment, setOpenComment] = useState<string | number | null>(null);

  const openHistory = useCallback(async (user: UserRow) => {
    setHistoryUser(user);
    setHistoryData([]);
    setHistorySearch('');
    setHistoryLoading(true);
    try {
      const { data: d } = await axios.get(`toils/user/${user.id}`);
      setHistoryData(d.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } catch {}
    finally { setHistoryLoading(false); }
  }, []);

  const filteredHistory = useMemo(() => {
    return historyData.filter(entry => {
      const d = entry.date?.slice(0, 10) ?? '';
      if (historyFrom && d < historyFrom) return false;
      if (historyTo && d > historyTo) return false;
      if (historySearch) {
        const q = historySearch.toLowerCase();
        if (!d.includes(q) && !String(entry.source ?? '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [historyData, historyFrom, historyTo, historySearch]);

  const historyBalance = useMemo(() =>
    filteredHistory.reduce((s: number, e: any) => s + (e.total_sum ?? 0), 0),
    [filteredHistory]
  );

  // Activity filter and correction form state (per user id)
  const [actFilter, setActFilter]       = useState<Record<number, string | null>>({});
  const [corrValue, setCorrValue]       = useState<Record<number, string>>({});
  const [corrReason, setCorrReason]     = useState<Record<number, string>>({});
  const [corrReasonErr, setCorrReasonErr] = useState<Record<number, boolean>>({});
  const [corrSaving, setCorrSaving]     = useState<Record<number, boolean>>({});

  const toggleActFilter = (userId: number, status: string) => {
    setActFilter(p => ({ ...p, [userId]: p[userId] === status ? null : status }));
  };

  const saveCorrection = async (user: UserRow) => {
    const val = parseFloat(corrValue[user.id] ?? '');
    const reason = (corrReason[user.id] ?? '').trim();
    if (!reason) { setCorrReasonErr(p => ({ ...p, [user.id]: true })); return; }
    if (isNaN(val)) return;
    setCorrSaving(p => ({ ...p, [user.id]: true }));
    try {
      await axios.post('toils/add/', {
        userId: user.id,
        total_sum: val,
        source: 'manual',
        date: new Date().toISOString().slice(0, 10),
        description: reason,
      });
      load();
      setCorrValue(p => ({ ...p, [user.id]: '' }));
      setCorrReason(p => ({ ...p, [user.id]: '' }));
    } catch {}
    finally { setCorrSaving(p => ({ ...p, [user.id]: false })); }
  };

  const resetToil = (user: UserRow) => {
    setCorrValue(p => ({ ...p, [user.id]: String(-user.netToil) }));
  };

  const recountToil = async (user: UserRow) => {
    setRecounting(p => ({ ...p, [user.id]: true }));
    try {
      // snapshot before recount
      const snapshot = user.weeks
        .filter(c => c.totalHours !== null)
        .map(c => ({ weekKey: c.weekKey, totalHours: c.totalHours, toilDelta: c.toilDelta }));

      await axios.get(`toils/recountToils/${user.id}`);

      // fetch fresh data and compare
      const { data: freshData } = await axios.get<CalendarData>(`toils/calendar?weeks=${WEEKS}&offset=${offset}`);
      setData(freshData);

      const freshUser = freshData.users.find(u => u.id === user.id);
      const diffs: string[] = [];

      if (freshUser) {
        for (const old of snapshot) {
          const fresh = freshUser.weeks.find(c => c.weekKey === old.weekKey);
          if (fresh && fresh.totalHours !== null && Math.abs((fresh.totalHours ?? 0) - (old.totalHours ?? 0)) > 0.01) {
            const wNum = old.weekKey.split('-W')[1];
            diffs.push(`W${wNum}: ${old.totalHours}h → ${fresh.totalHours}h`);
          }
        }
      }

      if (diffs.length > 0) {
        diffs.forEach(msg => enqueueSnackbar(`Fixed ${msg}`, { variant: 'warning', autoHideDuration: 6000 }));
      } else {
        enqueueSnackbar('Recalculation complete — no discrepancies found', { variant: 'success' });
      }
    } catch {
      enqueueSnackbar('Error recalculating TOIL', { variant: 'error' });
    } finally {
      setRecounting(p => ({ ...p, [user.id]: false }));
    }
  };

  const load = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    try {
      const { data: d } = await axios.get(`toils/calendar?weeks=${WEEKS}&offset=${offset}`);
      setData(d);
    } catch {}
    finally { setLoading(false); }
  }, [auth, offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setExpanded(null); }, [offset]);

  // Period label — computed from data (empty string while loading)
  const firstW  = data?.weeks[0];
  const lastFilledW = data?.weeks.filter(w => !w.isCurrent).pop() ?? data?.weeks[data.weeks.length - 1];
  const periodLabel = firstW && lastFilledW
    ? `${firstW.start.slice(0, 7).replace('-', '/')} – ${lastFilledW.end.slice(0, 7).replace('-', '/')}`
    : '';

  // Notify parent of period label (hook always called, no conditional)
  useEffect(() => { if (onPeriodLabel) onPeriodLabel(periodLabel); }, [periodLabel, onPeriodLabel]);

  const toggle = (id: number) => setExpanded(e => e === id ? null : id);

  if (!access?.toil_enabled) return null;

  // Derived state — computed safely even when data is null (loading)
  const weeks    = data?.weeks  ?? [];
  const allUsers: UserRow[] = Array.isArray(data?.users) ? data!.users : [];

  const users = allUsers.filter(u => {
    if (searchFilter && searchFilter.length > 0) {
      if (!`${u.firstName} ${u.lastName}`.toLowerCase().includes(searchFilter.toLowerCase())) return false;
    }
    if (contractFilter && contractFilter.length > 0) {
      if (!contractFilter.includes(u.patternId ?? 0)) return false;
    }
    return true;
  });

  const weekTotals = weeks.map(wh => {
    const cells = users.map(u => u.weeks.find(c => c.weekKey === wh.weekKey));
    const total  = cells.filter(Boolean).length;
    const filled = cells.filter(c => c && c.status !== 'EMPTY' && c.status !== 'CURRENT').length;
    const pct    = total > 0 ? Math.round((filled / total) * 100) : 0;
    return { filled, total, pct };
  });

  return (
    <>
    {/* Single stable ts-card — never unmounts, no layout jump */}
    <div className="ts-card" style={{ overflow: 'hidden' }}>

      {(loading || !data) ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 13 }}>
          Loading calendar…
        </div>
      ) : (

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
          <thead>
            <tr>
              <th style={{
                position: 'sticky', left: 0, zIndex: 4,
                background: 'var(--bg-top)', padding: '10px 16px', textAlign: 'left',
                fontSize: 11, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color)',
                borderBottom: '1px solid var(--border-input)', borderRight: '1px solid var(--border-input)', minWidth: 210,
              }}>Employee</th>
              <th style={{
                position: 'sticky', left: 210, zIndex: 4,
                background: 'var(--bg-top)', padding: '10px 12px', textAlign: 'center',
                fontSize: 11, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color)',
                borderBottom: '1px solid var(--border-input)', borderRight: '1px solid var(--border-input)', minWidth: 80, whiteSpace: 'nowrap',
              }}>TOIL</th>
              {weeks.map(wh => (
                <th key={wh.weekKey} style={{
                  padding: '8px 4px 10px', textAlign: 'center', minWidth: 90,
                  fontSize: 11, fontWeight: 800, letterSpacing: '.04em',
                  background: 'var(--bg-top)',
                  color: wh.isCurrent ? 'var(--text-color)' : 'var(--text-color-calendar-week)',
                  borderBottom: '1px solid var(--border-input)',
                }}>
                  W{wh.weekNum}{wh.isCurrent ? ' · now' : ''}
                  <div style={{ fontSize: 9, fontWeight: 500, color: 'var(--text-color-calendar-week)', marginTop: 2, letterSpacing: 0 }}>
                    {wh.start.slice(5).replace('-', '/')} – {wh.end.slice(5).replace('-', '/')}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(user => {
              const isOpen = expanded === user.id;
              const maxH = Math.max(...user.weeks.map(c => c.totalHours ?? 0), user.hoursPerWeek ?? 40, 1);

              return (
                <React.Fragment key={user.id}>
                  {/* Main row */}
                  <tr onClick={() => toggle(user.id)} style={{ cursor: 'pointer' }}>
                    <td style={{
                      position: 'sticky', left: 0, zIndex: 2,
                      background: 'var(--btn-user-form)', borderRight: '1px solid var(--border-input)', borderBottom: '1px solid var(--border-input)', minWidth: 210, padding: 0,
                    }}>

                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                        background: isOpen ? 'var(--bg-top)' : 'transparent',
                        transition: 'background .12s',
                      }}>
                        <UserAvatar user={{ id: user.id, first_name: user.firstName, last_name: user.lastName }} size={32} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>{user.firstName} {user.lastName}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{user.jobTitle}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 5 }}>
                            {user.patternTitle && <span>{user.patternTitle}</span>}
                            {user.patternTitle && user.hoursPerWeek && <span style={{ opacity: .4 }}>·</span>}
                            {user.hoursPerWeek && <span>{user.hoursPerWeek}h/wk</span>}
                          </div>
                        </div>
                        <div style={{
                          marginLeft: 'auto', width: 18, height: 18, borderRadius: 5,
                          border: '1px solid var(--border-input)', background: isOpen ? 'var(--bg-input, var(--btn-user-form))' : 'var(--btn-user-form)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: isOpen ? 'var(--text-color)' : 'var(--text-color-calendar-week)',
                          transition: 'all .15s', transform: isOpen ? 'rotate(90deg)' : 'none', flexShrink: 0,
                        }}>›</div>
                      </div>
                    </td>
                    {/* Net TOIL column */}
                    <td style={{
                      position: 'sticky', left: 210, zIndex: 2,
                      background: 'var(--btn-user-form)', borderRight: '1px solid var(--border-input)', borderBottom: '1px solid var(--border-input)', minWidth: 80, padding: '0 12px', textAlign: 'center', verticalAlign: 'middle',
                    }}>
                      <div style={{ fontSize: 15, fontWeight: 900, fontVariantNumeric: 'tabular-nums', color: user.netToil > 0 ? '#059669' : user.netToil < 0 ? '#ef4444' : 'var(--text-color-calendar-week)' }}>
                        {user.netToil > 0 ? '+' : ''}{user.netToil}h
                      </div>
                    </td>
                    {user.weeks.map(cell => {
                      const normStatus = (cell.status ?? 'EMPTY').toUpperCase() as keyof typeof STATUS_COLORS;
                      const colors = STATUS_COLORS[normStatus] ?? STATUS_COLORS.EMPTY;
                      const delta = fmtDelta(cell.toilDelta);
                      const isEmpty = cell.status === 'EMPTY';
                      const isCur = cell.status === 'CURRENT';
                      return (
                        <td key={cell.weekKey} style={{ padding: '4px 5px', borderBottom: '1px solid var(--border-input)', borderRight: '1px solid var(--border-input)', verticalAlign: 'top' }}>
                          <div style={{
                            borderRadius: 5, padding: '6px 8px', minHeight: 50, position: 'relative',
                            background: colors.bg,
                            border: isEmpty ? '1.5px dashed var(--border-input)' : isCur ? '1.5px dashed var(--border-input)' : 'none',
                            display: 'flex', flexDirection: 'column', gap: 4,
                          }}>
                            {!isEmpty && !isCur && (
                              <div style={{ width: 6, height: 6, borderRadius: '50%', background: colors.dot, position: 'absolute', top: 7, right: 7 }} />
                            )}
                            {cell.totalHours !== null ? (
                              <>
                                {/* Hours logged */}
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                                  <span style={{ fontSize: 15, fontWeight: 900, fontVariantNumeric: 'tabular-nums', lineHeight: 1, color: colors.text }}>{fmt(cell.totalHours)}</span>
                                  {cell.hoursPerWeek != null && (
                                    <span style={{ fontSize: 9, fontWeight: 500, color: 'var(--text-color-calendar-week)', fontVariantNumeric: 'tabular-nums' }}>/{cell.hoursPerWeek}h</span>
                                  )}
                                </div>
                                {/* TOIL delta */}
                                <div style={{ fontSize: 10, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: delta.color }}>{delta.text}</div>
                              </>
                            ) : (
                              <div style={{ fontSize: isCur ? 10 : 12, fontWeight: isCur ? 500 : 700, color: colors.text, marginTop: 8, textAlign: 'center' }}>
                                {isCur ? 'In progress' : '—'}
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Detail row — variant 6 */}
                  {isOpen && (() => {
                    const curFilter = actFilter[user.id] ?? null;
                    // build activity rows: reverse to show newest first
                    const actRows = [...user.weeks]
                      .reverse()
                      .map(cell => ({ cell, wh: weeks.find(w => w.weekKey === cell.weekKey) }))
                      .filter(({ cell }) => {
                        const st = cell.status?.toUpperCase() ?? 'EMPTY';
                        if (curFilter) return st === curFilter;
                        return st !== 'EMPTY';
                      });

                    const statTiles = [
                      { key: 'FULFILLED', label: 'Approved', value: user.fulfilled, color: '#059669', bg: 'rgba(5,150,105,.09)', border: 'rgba(5,150,105,.22)' },
                      { key: 'PENDING',   label: 'Pending',  value: user.pending,   color: '#d97706', bg: 'rgba(217,119,6,.09)',  border: 'rgba(217,119,6,.22)' },
                      { key: 'REJECTED',  label: 'Rejected', value: user.rejected,  color: '#ef4444', bg: 'rgba(239,68,68,.08)',  border: 'rgba(239,68,68,.22)' },
                    ];

                    const statusLabel: Record<string, { text: string; color: string; bg: string }> = {
                      FULFILLED: { text: 'Approved', color: '#059669', bg: 'rgba(5,150,105,.12)' },
                      PENDING:   { text: 'Pending',  color: '#d97706', bg: 'rgba(217,119,6,.12)' },
                      REJECTED:  { text: 'Rejected', color: '#ef4444', bg: 'rgba(239,68,68,.10)' },
                      CURRENT:   { text: 'Current',  color: '#2563eb', bg: 'rgba(37,99,235,.09)' },
                    };

                    const canCorrect = access?.toil_edit_all === true || access?.toil_edit_all === 1;

                    return (
                      <tr>
                        <td colSpan={weeks.length + 2} style={{ padding: 0, background: 'var(--bg-form)', borderBottom: '1px solid var(--border-input)', borderTop: '1px solid var(--border-input)' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '190px 1fr auto' }}>

                            {/* ── LEFT: Summary ── */}
                            <div style={{ padding: '14px 16px', borderRight: '1px solid var(--border-input)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                              <div>
                                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', marginBottom: 4 }}>TOIL Balance</div>
                                <div style={{ fontSize: 28, fontWeight: 900, fontVariantNumeric: 'tabular-nums', letterSpacing: '-.03em', color: user.netToil >= 0 ? '#059669' : '#ef4444', lineHeight: 1 }}>
                                  {user.netToil >= 0 ? '+' : ''}{user.netToil}h
                                </div>
                                <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 3 }}>All-time balance</div>
                              </div>

                              {/* 3 stat tiles */}
                              <div style={{ display: 'flex', gap: 5 }}>
                                {statTiles.map(tile => {
                                  const isActive = curFilter === tile.key;
                                  return (
                                    <div
                                      key={tile.key}
                                      onClick={e => { e.stopPropagation(); toggleActFilter(user.id, tile.key); }}
                                      style={{
                                        flex: 1, padding: '7px 6px', borderRadius: 8, textAlign: 'center',
                                        cursor: 'pointer', transition: 'all .13s',
                                        background: isActive ? tile.bg : 'var(--background-color)',
                                        border: `1px solid ${isActive ? tile.border : 'var(--border-input)'}`,
                                        boxShadow: isActive ? `0 2px 8px ${tile.bg}` : 'none',
                                      }}
                                    >
                                      <div style={{ fontSize: 16, fontWeight: 900, color: tile.color, fontVariantNumeric: 'tabular-nums' }}>{tile.value}</div>
                                      <div style={{ fontSize: 9, fontWeight: 700, color: isActive ? tile.color : 'var(--text-color-calendar-week)', marginTop: 1, letterSpacing: '.04em' }}>{tile.label}</div>
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Recount button — only for admins */}
                              {canCorrect && (
                                <button
                                  type="button"
                                  disabled={recounting[user.id]}
                                  onClick={e => { e.stopPropagation(); recountToil(user); }}
                                  style={{
                                    height: 28, padding: '0 12px', borderRadius: 8,
                                    border: '1px solid rgba(37,99,235,.3)',
                                    background: recounting[user.id] ? 'rgba(37,99,235,.04)' : 'rgba(37,99,235,.07)',
                                    color: '#2563eb', fontSize: 11, fontWeight: 700,
                                    cursor: recounting[user.id] ? 'default' : 'pointer',
                                    opacity: recounting[user.id] ? .6 : 1,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                                    transition: 'all .15s',
                                  }}
                                >
                                  <span style={{ fontSize: 13, lineHeight: 1 }}>⟳</span>
                                  {recounting[user.id] ? 'Recalculating…' : 'Recalculate from reports'}
                                </button>
                              )}
                            </div>

                            {/* ── CENTER: Activity ── */}
                            <div style={{ borderRight: '1px solid var(--border-input)', display: 'flex', flexDirection: 'column' }}>
                              <div style={{ padding: '9px 14px 7px', borderBottom: '1px solid var(--border-input)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', flex: 1 }}>
                                  {curFilter ? `${statusLabel[curFilter]?.text ?? curFilter} weeks` : 'Recent activity'}
                                </div>
                                {curFilter && (
                                  <button type="button" onClick={e => { e.stopPropagation(); toggleActFilter(user.id, curFilter); }}
                                    style={{ fontSize: 9, padding: '2px 8px', borderRadius: 20, border: '1px solid var(--border-input)', background: 'var(--btn-user-form)', color: 'var(--text-color-calendar-week)', cursor: 'pointer' }}>
                                    ✕ Clear filter
                                  </button>
                                )}
                                <button type="button" onClick={e => { e.stopPropagation(); openHistory(user); }}
                                  style={{ height: 24, padding: '0 10px', borderRadius: 7, border: '1px solid var(--border-input)', background: 'var(--btn-user-form)', color: 'var(--text-color)', fontSize: 10, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                  Full History
                                </button>
                              </div>
                              <div style={{ overflowY: 'auto', maxHeight: 180, scrollbarWidth: 'thin', scrollbarColor: 'var(--border-input) transparent' }}>
                                {actRows.length === 0 ? (
                                  <div style={{ padding: '20px 14px', textAlign: 'center', fontSize: 11, color: 'var(--text-color-calendar-week)' }}>
                                    {curFilter ? 'No weeks with this status' : 'No activity'}
                                  </div>
                                ) : actRows.map(({ cell, wh }) => {
                                  const st = cell.status?.toUpperCase() ?? 'EMPTY';
                                  const sl = statusLabel[st] ?? { text: st, color: 'var(--text-color-calendar-week)', bg: 'var(--btn-user-form)' };
                                  const delta = fmtDelta(cell.toilDelta);
                                  return (
                                    <div key={cell.weekKey} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: '1px solid var(--border-input)', transition: 'background .1s', cursor: 'default' }}
                                      onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-top)'}
                                      onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                                      <div style={{ width: 34, height: 34, borderRadius: 8, background: sl.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <div style={{ fontSize: 10, fontWeight: 800, color: sl.color }}>W{wh?.weekNum}</div>
                                        {cell.totalHours !== null && <div style={{ fontSize: 8, color: sl.color, fontVariantNumeric: 'tabular-nums' }}>{cell.totalHours}h</div>}
                                      </div>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)' }}>
                                          {wh ? `${wh.start.slice(5).replace('-', '/')} – ${wh.end.slice(5).replace('-', '/')} ${wh.year}` : cell.weekKey}
                                        </div>
                                        <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>
                                          {cell.totalHours !== null ? `${cell.totalHours}h logged` : 'No data'}{cell.hoursPerWeek ? ` · target ${cell.hoursPerWeek}h` : ''}
                                        </div>
                                      </div>
                                      {cell.toilDelta !== null && (
                                        <div style={{ fontSize: 12, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: delta.color, flexShrink: 0 }}>{delta.text}</div>
                                      )}
                                      <div style={{ padding: '2px 8px', borderRadius: 20, fontSize: 9, fontWeight: 700, background: sl.bg, color: sl.color, flexShrink: 0 }}>{sl.text}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* ── RIGHT: Correction ── */}
                            <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, minWidth: 200 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)' }}>Correction</div>
                                {canCorrect && <div style={{ padding: '1px 6px', borderRadius: 10, background: 'rgba(124,58,237,.1)', color: '#7c3aed', fontSize: 8, fontWeight: 700 }}>admin</div>}
                              </div>

                              {!canCorrect ? (
                                <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', lineHeight: 1.5 }}>No permission to edit TOIL</div>
                              ) : (
                                <>
                                  <button type="button"
                                    onClick={e => { e.stopPropagation(); resetToil(user); }}
                                    style={{ height: 28, padding: '0 12px', borderRadius: 8, border: '1px solid rgba(239,68,68,.3)', background: 'rgba(239,68,68,.07)', color: '#ef4444', fontSize: 11, fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}>
                                    ⊘ Reset TOIL to 0
                                  </button>

                                  <div>
                                    <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-color-calendar-week)', marginBottom: 4 }}>
                                      TOIL adjustment
                                    </div>
                                    <input
                                      type="number" step="0.5"
                                      value={corrValue[user.id] ?? ''}
                                      onClick={e => e.stopPropagation()}
                                      onChange={e => { e.stopPropagation(); setCorrValue(p => ({ ...p, [user.id]: e.target.value })); }}
                                      placeholder="e.g. -3.5"
                                      style={{ width: '100%', height: 30, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 7, color: 'var(--text-color)', fontSize: 13, fontWeight: 700, padding: '0 10px', outline: 'none', fontVariantNumeric: 'tabular-nums' }}
                                    />
                                  </div>

                                  <div>
                                    <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <span style={{ color: corrReasonErr[user.id] ? '#ef4444' : 'var(--text-color-calendar-week)' }}>Reason</span>
                                      <span style={{ color: '#ef4444', fontSize: 10 }}>*</span>
                                    </div>
                                    <input
                                      type="text"
                                      value={corrReason[user.id] ?? ''}
                                      onClick={e => e.stopPropagation()}
                                      onChange={e => { e.stopPropagation(); setCorrReason(p => ({ ...p, [user.id]: e.target.value })); setCorrReasonErr(p => ({ ...p, [user.id]: false })); }}
                                      placeholder="Required reason..."
                                      style={{ width: '100%', height: 30, background: 'var(--bg-input, var(--btn-user-form))', border: `1px solid ${corrReasonErr[user.id] ? '#ef4444' : 'var(--border-input)'}`, borderRadius: 7, color: 'var(--text-color)', fontSize: 12, padding: '0 10px', outline: 'none' }}
                                    />
                                    {corrReasonErr[user.id] && <div style={{ fontSize: 9, color: '#ef4444', marginTop: 3 }}>Reason is required</div>}
                                  </div>

                                  <button type="button"
                                    disabled={corrSaving[user.id]}
                                    onClick={e => { e.stopPropagation(); saveCorrection(user); }}
                                    style={{ height: 30, borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#2563eb)', color: 'white', fontSize: 12, fontWeight: 700, cursor: corrSaving[user.id] ? 'default' : 'pointer', opacity: corrSaving[user.id] ? .7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                    {corrSaving[user.id] ? 'Saving…' : 'Apply correction'}
                                  </button>
                                </>
                              )}
                            </div>

                          </div>
                        </td>
                      </tr>
                    );
                  })()}
                </React.Fragment>
              );
            })}
          </tbody>

          {/* Summary footer */}
          <tfoot>
            <tr>
              <td className="bg-[var(--bg-top)]" style={{ position: 'sticky', left: 0, borderRight: '1px solid var(--border-input)', borderTop: '1px solid var(--border-input)', padding: '10px 16px' }}>

                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', marginBottom: 5 }}>Total · {periodLabel}</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {(['fulfilled','pending','rejected'] as const).map(k => {
                    const total = users.reduce((a, u) => a + (u as any)[k], 0);
                    const color = k === 'fulfilled' ? '#059669' : k === 'pending' ? '#d97706' : '#ef4444';
                    const dot = k === 'fulfilled' ? '#059669' : k === 'pending' ? '#d97706' : '#ef4444';
                    return (
                      <span key={k} style={{ fontSize: 10, fontWeight: 700, color, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, display: 'inline-block' }} />
                        {total} {k.charAt(0).toUpperCase() + k.slice(1)}
                      </span>
                    );
                  })}
                </div>
              </td>
              {/* Net TOIL total in footer */}
              <td style={{
                position: 'sticky', left: 210, zIndex: 2,
                borderTop: '1px solid var(--border-input)', borderRight: '1px solid var(--border-input)',
                background: 'var(--bg-top)', padding: '10px 12px', textAlign: 'center', verticalAlign: 'middle',
              }}>
                {(() => {
                  const total = users.reduce((s, u) => s + u.netToil, 0);
                  return <div style={{ fontSize: 14, fontWeight: 900, fontVariantNumeric: 'tabular-nums', color: total > 0 ? '#059669' : total < 0 ? '#ef4444' : 'var(--text-color-calendar-week)' }}>{total > 0 ? '+' : ''}{total}h</div>;
                })()}
              </td>
              {weekTotals.map((wt, i) => {
                const col = wt.pct === 100 ? '#059669' : wt.pct >= 80 ? '#d97706' : '#ef4444';
                const wh = weeks[i];
                return (
                  <td key={i} style={{ padding: '6px 5px', borderTop: '1px solid var(--border-input)', background: 'var(--bg-top)', textAlign: 'center' }}>
                    {!wh?.isCurrent ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: col }}>{wt.filled}/{wt.total}</div>
                        <div style={{ fontSize: 9, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>{wt.pct}%</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-color-calendar-week)' }}>—</div>
                        <div style={{ fontSize: 9, color: 'var(--text-color)', marginTop: 1 }}>current</div>
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
      )}   {/* end loading ? ... : (...) */}
    </div>   {/* end ts-card */}

      {/* ── Full History Drawer ── */}
      <SidePanel
        open={!!historyUser}
        onClose={() => setHistoryUser(null)}
        width={560}
        paperStyle={{ display: 'flex', flexDirection: 'column' }}
      >
        {historyUser && (
          <>
            {/* Header */}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-input)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg,${userColor(historyUser.id)})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                {initials(historyUser.firstName, historyUser.lastName)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-color)' }}>{historyUser.firstName} {historyUser.lastName}</div>
                <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{historyUser.jobTitle}</div>
              </div>
              <button type="button" onClick={() => setHistoryUser(null)}
                style={{ width: 26, height: 26, borderRadius: 7, border: '1px solid var(--border-input)', background: 'var(--btn-user-form)', color: 'var(--text-color)', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>

            {/* Balance summary */}
            <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border-input)', background: 'var(--btn-user-form)', flexShrink: 0, display: 'flex', gap: 20 }}>
              <div>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', marginBottom: 3 }}>Net TOIL (filtered)</div>
                <div style={{ fontSize: 22, fontWeight: 900, fontVariantNumeric: 'tabular-nums', color: historyBalance >= 0 ? '#059669' : '#ef4444' }}>
                  {historyBalance >= 0 ? '+' : ''}{Math.round(historyBalance * 100) / 100}h
                </div>
              </div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', marginBottom: 3 }}>Entries</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--text-color)' }}>{filteredHistory.length}</div>
              </div>
            </div>

            {/* Filters */}
            <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border-input)', flexShrink: 0, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-color-calendar-week)', marginBottom: 4 }}>Search</div>
                <input value={historySearch} onChange={e => setHistorySearch(e.target.value)} placeholder="date or source..."
                  style={{ width: '100%', height: 28, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 7, color: 'var(--text-color)', fontSize: 12, padding: '0 9px', outline: 'none' }} />
              </div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-color-calendar-week)', marginBottom: 4 }}>From</div>
                <CalendarPicker mode="single" variant="field" output="string" value={historyFrom} onChange={(v) => setHistoryFrom(v)} />
              </div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-color-calendar-week)', marginBottom: 4 }}>To</div>
                <CalendarPicker mode="single" variant="field" output="string" value={historyTo} onChange={(v) => setHistoryTo(v)} />
              </div>
              {(historyFrom || historyTo || historySearch) && (
                <button type="button" onClick={() => { setHistorySearch(''); setHistoryFrom(''); setHistoryTo(''); }}
                  style={{ height: 28, padding: '0 10px', borderRadius: 7, border: '1px solid var(--border-input)', background: 'var(--btn-user-form)', color: 'var(--text-color-calendar-week)', fontSize: 11, cursor: 'pointer', alignSelf: 'flex-end' }}>
                  Clear
                </button>
              )}
            </div>

            {/* Table */}
            <div style={{ flex: 1, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'var(--border-input) transparent' }}>
              {historyLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 13 }}>Loading…</div>
              ) : filteredHistory.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 13 }}>No entries</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--btn-user-form)', position: 'sticky', top: 0, zIndex: 1 }}>
                      <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)', whiteSpace: 'nowrap' }}>Date</th>
                      <th style={{ padding: '8px 8px', textAlign: 'left', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)', whiteSpace: 'nowrap' }}>Week</th>
                      <th style={{ padding: '8px 8px', textAlign: 'left', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)', whiteSpace: 'nowrap' }}>Source</th>
                      <th style={{ padding: '8px 8px', textAlign: 'right', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)', whiteSpace: 'nowrap' }}>Logged</th>
                      <th style={{ padding: '8px 8px', textAlign: 'right', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)', whiteSpace: 'nowrap' }}>Contract</th>
                      <th style={{ padding: '8px 14px 8px 0', textAlign: 'right', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)', whiteSpace: 'nowrap' }}>TOIL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.map((entry: any, i: number) => {
                      const delta = entry.total_sum ?? 0;
                      const isReport = entry.source === 'report';
                      const logged = entry.total_hours != null ? `${entry.total_hours}h` : '—';
                      const contract = entry.hours_per_week != null ? `${entry.hours_per_week}h` : '—';
                      const rowKey = entry.id ?? i;
                      const comment = (entry.description ?? '').trim();
                      const commentOpen = openComment === rowKey;
                      return (
                        <React.Fragment key={rowKey}>
                        <tr style={{ borderBottom: comment && commentOpen ? 'none' : '1px solid var(--border-input)' }}>
                          <td style={{ padding: '9px 14px', fontSize: 13, color: 'var(--text-color)', fontVariantNumeric: 'tabular-nums', fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {entry.date?.slice(0, 10) ?? '—'}
                          </td>
                          <td style={{ padding: '9px 8px', fontSize: 12, color: 'var(--text-color-calendar-week)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {entry.date ? `W${getISOWeek(new Date(entry.date))}` : '—'}
                          </td>
                          <td style={{ padding: '9px 8px', fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                                background: isReport ? 'rgba(37,99,235,.1)' : 'rgba(124,58,237,.1)',
                                color: isReport ? '#2563eb' : '#7c3aed' }}>
                                {isReport ? 'Report' : 'Manual'}
                              </span>
                              {comment && (
                                <button type="button"
                                  onClick={() => setOpenComment(prev => prev === rowKey ? null : rowKey)}
                                  title="Comment"
                                  aria-label="Comment"
                                  style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0,
                                    opacity: commentOpen ? 1 : .55 }}>
                                  💬
                                </button>
                              )}
                            </span>
                          </td>
                          <td style={{ padding: '9px 8px', textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                            color: isReport ? 'var(--text-color)' : 'var(--text-color-calendar-week)' }}>
                            {isReport ? logged : '—'}
                          </td>
                          <td style={{ padding: '9px 8px', textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums',
                            color: 'var(--text-color-calendar-week)' }}>
                            {isReport ? contract : '—'}
                          </td>
                          <td style={{ padding: '9px 14px 9px 0', textAlign: 'right', fontSize: 14, fontWeight: 900, fontVariantNumeric: 'tabular-nums',
                            color: delta > 0 ? '#059669' : delta < 0 ? '#ef4444' : 'var(--text-color-calendar-week)' }}>
                            {delta > 0 ? '+' : ''}{delta}h
                          </td>
                        </tr>
                        {comment && commentOpen && (
                          <tr style={{ borderBottom: '1px solid var(--border-input)' }}>
                            <td colSpan={6} style={{ padding: '2px 14px 10px 14px' }}>
                              <div style={{ background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, color: 'var(--text-color)', whiteSpace: 'pre-wrap' }}>
                                {comment}
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </SidePanel>
    </>
  );
};

export default ToilCalendar;
